import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../commons/Button/Button";
import { Dropdown } from "../../commons/Dropdown/Dropdown";
import { FormField } from "../../commons/FormField/FormField";
import "../../css/ScrollbarStyles.css";
import * as yup from "yup";
import useCreateInternalSubscription from "../../state/hooks/subscriptions/useCreateInternalSubscription";
import { premiumPackageOptions } from "../../models/enum/package.enum";

// Using premiumPackageOptions from centralized enum (excludes Free)

// Validation schema
const validationSchema = yup.object({
    subUserId: yup.string().required("Sub User ID is required"),
    package: yup.string().required("Package is required"),
    nextBillingDate: yup.number()
        .required("Next billing date is required")
        .positive("Next billing date must be a valid timestamp"),
});

const NewSubscription = () => {
    const navigate = useNavigate();
    const createInternalSubscription = useCreateInternalSubscription();

    const formik = useFormik({
        initialValues: {
            subUserId: "",
            package: "",
            nextBillingDate: 0,
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                const result = await createInternalSubscription.mutateAsync(values);
                
                // Reset form after successful submission
                formik.resetForm();
                
                // Optional: Navigate to subscriptions list after successful creation
                setTimeout(() => {
                    navigate('/subscriptions');
                }, 1500);
            } catch (error) {
                // Error handling is done in the mutation hook
                console.error("Form submission error:", error);
            }
        },
    });

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateValue = e.target.value;
        if (dateValue) {
            // Convert to Unix timestamp in seconds (backend expects seconds, not milliseconds)
            const timestamp = Math.floor(new Date(dateValue).getTime() / 1000);
            formik.setFieldValue("nextBillingDate", timestamp);
        } else {
            formik.setFieldValue("nextBillingDate", 0);
        }
    };


    return (
        <div className="flex overflow-x-auto px-30 pt-80 pb-20 flex-col bg-light-grey-1 h-full">
            <h2 className="text-dark-grey font-bold text-2xl">Create Internal Subscription</h2>
            
            {/* Status Display */}
            {createInternalSubscription.isSuccess && (
                <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                    ✅ Subscription created successfully! 
                    {createInternalSubscription.data?.subscriptionId && (
                        <span> (ID: {createInternalSubscription.data.subscriptionId})</span>
                    )}
                </div>
            )}
            
            {createInternalSubscription.isError && (
                <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    ❌ Error: {createInternalSubscription.error?.message || "Failed to create subscription"}
                </div>
            )}
            
            <div className="mt-50 mb-30">
                <form onSubmit={formik.handleSubmit}>
                    <FormField label="Sub User ID">
                        <input
                            className="h-12 w-360 px-3 border border-gray-300 rounded-md"
                            placeholder="Enter Sub User ID"
                            type="text"
                            id="subUserId"
                            name="subUserId"
                            value={formik.values.subUserId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.subUserId && formik.errors.subUserId && (
                            <div className="text-red-500 text-sm mt-1">
                                {formik.errors.subUserId}
                            </div>
                        )}
                    </FormField>

                    <FormField label="Package">
                        <Dropdown 
                            options={premiumPackageOptions} 
                            className="w-360" 
                            id="package"
                            onChange={(option) => {
                                formik.setFieldValue("package", option?.value || "");
                            }}
                            value={premiumPackageOptions.find(opt => opt.value === formik.values.package)}
                        />
                        {formik.touched.package && formik.errors.package && (
                            <div className="text-red-500 text-sm mt-1">
                                {formik.errors.package}
                            </div>
                        )}
                    </FormField>

                    <FormField label="Next Billing Date">
                        <input
                            className="h-12 w-360 px-3 border border-gray-300 rounded-md"
                            type="datetime-local"
                            id="nextBillingDate"
                            name="nextBillingDate"
                            onChange={handleDateChange}
                            min={new Date().toISOString().slice(0, 16)}
                            value={
                                formik.values.nextBillingDate &&
                                !Number.isNaN(new Date(formik.values.nextBillingDate * 1000).getTime())
                                    ? new Date(formik.values.nextBillingDate * 1000).toISOString().slice(0, 16)
                                    : ''
                            }
                        />
                        {formik.touched.nextBillingDate && formik.errors.nextBillingDate && (
                            <div className="text-red-500 text-sm mt-1">
                                {formik.errors.nextBillingDate}
                            </div>
                        )}
                    </FormField>

                    <div className="mt-8 flex gap-4">
                        <Button 
                            type="main" 
                            onClick={() => formik.handleSubmit()}
                            disabled={createInternalSubscription.isPending || !formik.isValid}
                        >
                            {createInternalSubscription.isPending ? "Creating..." : "Create Subscription"}
                        </Button>
                        <Button 
                            type="secondary" 
                            onClick={() => {
                                formik.resetForm();
                            }}
                        >
                            Reset
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewSubscription;